import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Movie } from '../../types';
import { movieService, apiCall } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import './MoviePlayer.css';

// Bunny.net library ID
const BUNNY_LIBRARY_ID = '555414';

const MoviePlayer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user, subscription, checkSubscription } = useAuth();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  const [movie, setMovie] = useState<Movie | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format duration
  const formatDuration = (mins: number | string | undefined) => {
    if (mins === undefined || mins === null) return '—';
    const m = parseInt(String(mins));
    if (!m || isNaN(m)) return '—';
    const h = Math.floor(m / 60);
    const min = m % 60;
    return h > 0 ? `${h}h ${min}m` : `${min}m`;
  };

  // Extract Bunny GUID from video URL
  const extractBunnyGuid = (url: string): string | null => {
    if (!url) return null;

    // Pattern 1: https://vz-xxx.b-cdn.net/{guid}/playlist.m3u8
    let match = url.match(/\/([a-f0-9-]{36})\//i);
    if (match) return match[1];

    // Pattern 2: https://iframe.mediadelivery.net/embed/{libraryId}/{guid}
    match = url.match(/embed\/\d+\/([a-f0-9-]{36})/i);
    if (match) return match[1];

    // If it looks like a GUID itself
    if (/^[a-f0-9-]{36}$/i.test(url)) {
      return url;
    }

    return null;
  };

  // Check access and load movie
  useEffect(() => {
    const checkAccessAndLoad = async () => {
      if (!id) {
        setError('Invalid movie ID');
        setCheckingAccess(false);
        setLoading(false);
        return;
      }

      // Check authentication
      if (!isAuthenticated) {
        navigate(`/login?redirect=/watch/${id}`);
        return;
      }

      try {
        setCheckingAccess(true);

        // Admin bypass
        if (user?.role === 'admin') {
          setHasAccess(true);
          setCheckingAccess(false);
          loadMovie();
          return;
        }

        // Check subscription and get fresh status
        const subStatus = await checkSubscription();
        
        if (subStatus?.has_subscription) {
          setHasAccess(true);
          setCheckingAccess(false);
          loadMovie();
        } else {
          setHasAccess(false);
          setCheckingAccess(false);
        }
      } catch (err: any) {
        console.error('Access check error:', err);
        setHasAccess(false);
        setCheckingAccess(false);
      }
    };

    const loadMovie = async () => {
      try {
        setLoading(true);
        const movieData = await movieService.getMovie(parseInt(id!));
        setMovie(movieData);
        
        // Increment view count
        try {
          await apiCall(`/movies/${id}/watch`, { method: 'POST' });
        } catch (e) {
          console.error('Failed to increment view count:', e);
        }
        
        setError(null);
      } catch (err: any) {
        console.error('Error loading movie:', err);
        setError(err.message || 'Failed to load movie');
      } finally {
        setLoading(false);
      }
    };

    checkAccessAndLoad();
  }, [id, isAuthenticated, user, navigate, checkSubscription]);

  // Access check screen
  if (checkingAccess) {
    return (
      <div className="player-access-check">
        <div className="text-center">
          <div className="access-spinner"></div>
          <p className="text-white text-lg mt-4">Checking subscription...</p>
        </div>
      </div>
    );
  }

  // No access - show subscription required
  if (!hasAccess) {
    return (
      <div className="player-access-denied">
        <div className="access-denied-content">
          <i className="fas fa-lock text-6xl text-netflix-red mb-6"></i>
          <h1 className="text-3xl font-bold text-white mb-4">🔒 Subscription Required</h1>
          <p className="text-gray-400 text-lg mb-6">
            Subscription required to watch movies
          </p>
          <p className="text-gray-500 mb-8">Redirecting to subscription page...</p>
          <Link
            to="/subscribe"
            className="inline-block py-4 px-8 bg-gradient-to-r from-netflix-red to-red-600 rounded-xl text-lg font-bold"
          >
            🎁 Start 14-Day Free Trial
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="player-loading">
        <div className="text-center">
          <div className="player-spinner"></div>
          <p className="text-white mt-4">Loading movie...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !movie) {
    return (
      <div className="player-error">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-6xl text-netflix-red mb-4"></i>
          <h2 className="text-2xl font-bold text-white mb-4">{error || 'Movie not found'}</h2>
          <Link
            to="/movies"
            className="inline-block py-3 px-6 bg-netflix-red hover:bg-red-700 rounded-lg font-bold"
          >
            Back to Movies
          </Link>
        </div>
      </div>
    );
  }

  // Get video GUID
  const videoUrl = movie.video_url || '';
  const videoGuid = extractBunnyGuid(videoUrl);

  if (!videoGuid) {
    return (
      <div className="player-error">
        <div className="text-center">
          <i className="fas fa-video-slash text-6xl text-netflix-red mb-4"></i>
          <h2 className="text-2xl font-bold text-white mb-4">Video Not Available</h2>
          <p className="text-gray-400 mb-4">This movie's video is not configured properly.</p>
          <Link
            to="/movies"
            className="inline-block py-3 px-6 bg-netflix-red hover:bg-red-700 rounded-lg font-bold"
          >
            Back to Movies
          </Link>
        </div>
      </div>
    );
  }

  const iframeSrc = `https://iframe.mediadelivery.net/embed/${BUNNY_LIBRARY_ID}/${videoGuid}?autoplay=false&preload=true`;

  return (
    <div className="player-page">
      <div className="player-container">
        {/* Player Wrapper */}
        <div className="player-wrapper">
          <div className="iframe-container" id="iframeContainer">
            {/* CINECLUBS Branding */}
            <div className="player-branding">
              <img src="/public/assets/images/paylogo.png" alt="CINECLUBS" />
              <span>CINECLUBS</span>
            </div>
            
            {/* Bunny Player iframe */}
            <iframe
              ref={iframeRef}
              src={iframeSrc}
              loading="lazy"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title={movie.title}
            />
          </div>
        </div>

        {/* Movie Info */}
        <div className="player-info">
          <h1 id="title">{movie.title}</h1>
          <div className="player-meta">
            <span>
              <i className="fas fa-calendar"></i> {movie.release_year}
            </span>
            <span>
              <i className="fas fa-clock"></i> {formatDuration(movie.duration)}
            </span>
            <span>
              <i className="fas fa-tag"></i> {movie.genre}
            </span>
            <span className="inline-flex items-center bg-yellow-400 text-black px-2 py-1 rounded text-sm font-bold">
              <span className="font-black mr-1">IMDb</span>
              <i className="fas fa-star text-black text-xs mr-1"></i>
              {movie.rating}
            </span>
            <span>
              <i className="fas fa-eye"></i> {movie.view_count?.toLocaleString() || 0}
            </span>
          </div>
          <p className="player-desc">{movie.description}</p>
        </div>
      </div>
    </div>
  );
};

export default MoviePlayer;
